import React, { useState, useEffect } from 'react';
import styled from "styled-components";
import { useForm, FormProvider, SubmitHandler } from "react-hook-form";
import TextField from '../../Components/FormGroup/TextField';
import SelectField from '../../Components/FormGroup/SelectField';
import DateField from '../../Components/FormGroup/DateField';
import Button from '../../Components/Button';
import { LicenceLinkType, NamedDriverType } from '../../Components/Types';
import { getDateString } from '../../Components/Functions';
import { Form, Row, Col } from 'reactstrap';
import { genderOptions, issueByOptions, maxDriverAge, minDriverAge, toastConfig } from '../../Components/Constants';
import { validateEmail, validatePostcode } from '../../Components/FormGroup/Validator';
import { useMsal } from '@azure/msal-react';
import { Header } from 'Components/PageComponents';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { LicenceLinkAPI } from 'API/LicenceLinkAPI';

type LicenceLinkFormProps = {
    driver: NamedDriverType;
}
const Prompt = styled.p`
    font-family: Lato-Bold;
    font-size:1vw;
`;
function LicenseCheckPage({ driver }: LicenceLinkFormProps) {
    const navigate = useNavigate()
    const [disabled, setDisabled]: any = useState<boolean>(false);
    const [isUkLicence, setIsUkLicence] = useState<boolean>(false);
    const [driverSubmitted, setDriverSubmitted] = useState<boolean>(false);
    const methods = useForm<LicenceLinkType>();
    
    const maxDriverAgeDate = new Date();
    maxDriverAgeDate.setFullYear(maxDriverAgeDate.getFullYear() - maxDriverAge);

    const minDriverAgeDate = new Date();
    minDriverAgeDate.setFullYear(minDriverAgeDate.getFullYear() - minDriverAge);
    const { instance } = useMsal();
    const { currentEmployee  } = useSelector((state: RootState) => state.auth);

    const { bookingId } = useParams<{ bookingId: string}>();
    
    useEffect(() => {
        const getEmployeeDetails = async () => {

            methods.reset({
                forename: driver.firstname,
                surname: driver.lastname,
                dob: driver.dob ? new Date(Date.parse(driver.dob.toString())) : null,
                email: driver.email,
                address1: driver.main_driver ? currentEmployee?.home_address1 : undefined,
                address2: driver.main_driver ? currentEmployee?.home_address2 : undefined,
                address3: driver.main_driver ? currentEmployee?.home_address3 : undefined,
                address4: driver.main_driver ? currentEmployee?.home_address4 : undefined,
                postcode: driver.main_driver ? currentEmployee?.home_postcode : undefined,
            })
        }
        getEmployeeDetails();
    }, [driver]);

    const onSubmit: SubmitHandler<LicenceLinkType> = data => {
        setDisabled(true);
        const formData = {
            booking_id: bookingId,
            named_driver_id: driver.named_driver_id,
            forename: data.forename,
            surname: data.surname,
            gender: data.gender,
            dateOfBirth: data.dob instanceof Date ? getDateString(data.dob) : '1900-01-01',
            licenceNumber: data.licenceNumber,
            licence_issued_by: data.licence_issued_by,
            emailAddress: data.email,
            address1: data.address1,
            address2: data.address2,
            address3: data.address4,
            town: data.address3,
            postcode: data.postcode,
            employeeNumber: currentEmployee?.employee_id.toString(),
            createdBy: currentEmployee?.email 
        };
        sendLLMandate(formData);
    }

    const sendLLMandate = async (data: any) => {
        try {
            const response = await LicenceLinkAPI.add(instance, data);
            let message = response.message;
            if (data.licence_issued_by == "Other") {
                message = "Unfortunately we are unable to check Non-UK driving licences via our partner Licence Link. In order to be added to the insurance of this vehicle, please send a copy of your driving licence to: TBC and include this reference: " + bookingId + ". Thank you";
            }
            toast.success(message, { ...toastConfig, toastId: message + "-toast" });
            setDriverSubmitted(true);
        }
        catch(e:any) {
            const message = e.response.data;
            if (message.includes("Licence number")) {
                methods.setError("licenceNumber", {type: 'pattern'});
            }
            setDriverSubmitted(false);
        }
        setDisabled(false);
    }

    return (
        <>
        {driver.mandate_status < 3 ?
            <FormProvider {...methods}>
                <Form onSubmit={methods.handleSubmit(onSubmit)}>
                    <Header>{driver.main_driver ? "Main Driver" : "Additional Driver"}</Header>
                    <Row>
                        <Col md={6}>
                            <TextField name="forename" label="Firstname" disabled={driver.firstname ? driver.main_driver == true : false} maxlength={35} required={true} />
                        </Col>
                        <Col md={6}>
                            <TextField name="surname" label="Lastname" disabled={currentEmployee?.lastname ? driver.main_driver == true : false} maxlength={35} required={true} />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <SelectField name="gender" label="Gender" options={genderOptions} required={true} />
                        </Col>
                        <Col md={6}>
                            <DateField name="dob" label="DOB" required={true} disabled={driver.dob ? driver.main_driver == true : false} minDate={maxDriverAgeDate} maxDate={minDriverAgeDate} />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <SelectField name="licence_issued_by" label="Licence Issued By" options={issueByOptions} required={true} onChange={(e) => setIsUkLicence(e.target.value == "UK")} />
                        </Col>
                        <Col md={6}>
                            <TextField name="licenceNumber" label="Licence Number" maxlength={isUkLicence ? 16 : undefined} required={true} css={{textTransform: 'uppercase'}} />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <TextField name="email" label="Email Address" disabled={driver.email ? driver.main_driver == true : false} validation={validateEmail} />
                        </Col>
                        <Col md={6}>
                            <TextField name="address1" label="Home Address 1" disabled={currentEmployee?.home_address1 ? driver.main_driver == true : false} required={true} />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <TextField name="address2" label="Home Address 2" disabled={currentEmployee?.home_address2 ? driver.main_driver == true : false} required={true}/>
                        </Col>
                        <Col md={6}>
                            <TextField name="address3" label="Town" disabled={currentEmployee?.home_address3 ? driver.main_driver == true : false} required={true} />
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <TextField name="address4" label="County" disabled={currentEmployee?.home_address4 ? driver.main_driver == true : false} required={true} />
                        </Col>
                        <Col md={6}>
                            <TextField name="postcode" label="Postcode" disabled={currentEmployee?.home_postcode ? driver.main_driver == true : false} validation={validatePostcode} required={true} />
                        </Col>
                    </Row>
                    {driverSubmitted ?
                        <Button colour="dark" type="button" text="ok" css={{ marginTop: "1vw" }} onClick={() => navigate("/MyAccount/Bookings")} />
                        :
                        <Button colour="dark" type="submit" text="submit" css={{ marginTop: "1vw" }} disabled={disabled}/>
                    }
                </Form>
            </FormProvider>
            :
            <>
                <Header>{driver.main_driver ? "Main Driver" : "Additional Driver"}</Header>
                <Prompt>Licence Check has already been submitted for this driver</Prompt>
            </>
        }
        </>
    );
}
export default LicenseCheckPage;
